import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { PublishedEpisode } from 'shared/data/episodes';
import { getPublishedEpisodes } from 'shared/data/episodeRepository';

export interface AudioPlayerState {
  currentEpisode: PublishedEpisode | null;
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  volume: number;
  isMuted: boolean;
  isLoading: boolean;
  error: string | null;
  inlineVisible: boolean;
  playbackRate: number;
  hasOlderEpisode: boolean;
}

export interface AudioPlayerControls {
  play: (episode: PublishedEpisode) => void;
  pause: () => void;
  togglePlayPause: () => void;
  seek: (seconds: number) => void;
  skipForward: (seconds?: number) => void;
  skipBackward: (seconds?: number) => void;
  setVolume: (v: number) => void;
  toggleMute: () => void;
  close: () => void;
  setInlineVisible: (value: boolean) => void;
  setPlaybackRate: (rate: number) => void;
  playOlderEpisode: () => void;
}

export const AudioPlayerStateContext = createContext<AudioPlayerState | null>(null);
export const AudioPlayerControlsContext = createContext<AudioPlayerControls | null>(null);

const DEFAULT_SKIP_FORWARD = 30;
const DEFAULT_SKIP_BACKWARD = 10;
const DEFAULT_VOLUME = 1;
const DEFAULT_PLAYBACK_RATE = 1;

export function AudioPlayerProvider({ children }: { children: ReactNode }) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const currentEpisodeRef = useRef<PublishedEpisode | null>(null);
  const playbackRateRef = useRef<number>(DEFAULT_PLAYBACK_RATE);

  const [currentEpisode, setCurrentEpisode] = useState<PublishedEpisode | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolumeState] = useState(DEFAULT_VOLUME);
  const [isMuted, setIsMuted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [announcement, setAnnouncement] = useState('');
  const [inlineVisible, setInlineVisibleState] = useState(false);
  const [playbackRate, setPlaybackRateState] = useState<number>(DEFAULT_PLAYBACK_RATE);

  useEffect(() => {
    currentEpisodeRef.current = currentEpisode;
  }, [currentEpisode]);

  useEffect(() => {
    playbackRateRef.current = playbackRate;
  }, [playbackRate]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onTimeUpdate = () => setCurrentTime(audio.currentTime);
    const onLoadedMetadata = () => {
      setDuration(Number.isFinite(audio.duration) ? audio.duration : 0);
      setIsLoading(false);
      audio.playbackRate = playbackRateRef.current;
    };
    const onPlay = () => {
      setIsPlaying(true);
      setError(null);
    };
    const onPause = () => setIsPlaying(false);
    const onEnded = () => {
      setIsPlaying(false);
      setCurrentTime(audio.duration || 0);
    };
    const onWaiting = () => setIsLoading(true);
    const onCanPlay = () => setIsLoading(false);
    const onPlaying = () => setIsLoading(false);
    const onError = () => {
      setIsPlaying(false);
      setIsLoading(false);
      setError('Audio failed to load');
    };
    const onVolumeChange = () => {
      setVolumeState(audio.volume);
      setIsMuted(audio.muted);
    };
    const onRateChange = () => {
      setPlaybackRateState(audio.playbackRate);
    };

    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('loadedmetadata', onLoadedMetadata);
    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('waiting', onWaiting);
    audio.addEventListener('canplay', onCanPlay);
    audio.addEventListener('playing', onPlaying);
    audio.addEventListener('error', onError);
    audio.addEventListener('volumechange', onVolumeChange);
    audio.addEventListener('ratechange', onRateChange);

    return () => {
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('loadedmetadata', onLoadedMetadata);
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('waiting', onWaiting);
      audio.removeEventListener('canplay', onCanPlay);
      audio.removeEventListener('playing', onPlaying);
      audio.removeEventListener('error', onError);
      audio.removeEventListener('volumechange', onVolumeChange);
      audio.removeEventListener('ratechange', onRateChange);
    };
  }, []);

  const play = useCallback((episode: PublishedEpisode) => {
    const audio = audioRef.current;
    if (!audio) return;
    if (!episode.audioUrl) {
      setError('No audio available for this episode');
      return;
    }

    const isSameEpisode = currentEpisodeRef.current?.buzzsproutId === episode.buzzsproutId;

    if (!isSameEpisode) {
      audio.src = episode.audioUrl;
      audio.playbackRate = playbackRateRef.current;
      setCurrentEpisode(episode);
      setCurrentTime(0);
      setDuration(0);
      setError(null);
      setIsLoading(true);
      setAnnouncement(`Now playing: ${episode.title}`);
      setMediaSessionMetadata(episode);
    }

    const playPromise = audio.play();
    if (playPromise) {
      playPromise.catch((err) => {
        console.error('[AudioPlayer] audio.play() rejected', err);
        setError('Playback was blocked or failed');
        setIsPlaying(false);
      });
    }
  }, []);

  const pause = useCallback(() => {
    audioRef.current?.pause();
  }, []);

  const togglePlayPause = useCallback(() => {
    const audio = audioRef.current;
    if (!audio || !currentEpisodeRef.current) return;
    if (audio.paused) {
      const result = audio.play();
      result?.catch((err) => {
        console.error('[AudioPlayer] audio.play() rejected', err);
        setError('Playback was blocked or failed');
      });
    } else {
      audio.pause();
    }
  }, []);

  const seek = useCallback((seconds: number) => {
    const audio = audioRef.current;
    if (!audio) return;
    const clamped = Math.max(0, Math.min(seconds, audio.duration || seconds));
    audio.currentTime = clamped;
    setCurrentTime(clamped);
  }, []);

  const skipForward = useCallback((seconds: number = DEFAULT_SKIP_FORWARD) => {
    const audio = audioRef.current;
    if (!audio) return;
    seek(audio.currentTime + seconds);
  }, [seek]);

  const skipBackward = useCallback((seconds: number = DEFAULT_SKIP_BACKWARD) => {
    const audio = audioRef.current;
    if (!audio) return;
    seek(audio.currentTime - seconds);
  }, [seek]);

  const setVolume = useCallback((v: number) => {
    const audio = audioRef.current;
    if (!audio) return;
    const clamped = Math.max(0, Math.min(1, v));
    audio.volume = clamped;
    if (clamped > 0 && audio.muted) audio.muted = false;
  }, []);

  const toggleMute = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.muted = !audio.muted;
  }, []);

  const close = useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.removeAttribute('src');
      audio.load();
    }
    setCurrentEpisode(null);
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    setIsLoading(false);
    setError(null);
    setAnnouncement('');
    if ('mediaSession' in navigator) {
      navigator.mediaSession.metadata = null;
      navigator.mediaSession.playbackState = 'none';
    }
  }, []);

  const setInlineVisible = useCallback((value: boolean) => {
    setInlineVisibleState(value);
  }, []);

  const setPlaybackRate = useCallback((rate: number) => {
    const audio = audioRef.current;
    const clamped = Math.max(0.25, Math.min(4, rate));
    playbackRateRef.current = clamped;
    setPlaybackRateState(clamped);
    if (audio) audio.playbackRate = clamped;
  }, []);

  const playOlderEpisode = useCallback(() => {
    const current = currentEpisodeRef.current;
    if (!current) return;
    const list = getPublishedEpisodes();
    const index = list.findIndex((ep) => ep.buzzsproutId === current.buzzsproutId);
    if (index < 0) return;
    const older = list[index + 1];
    if (!older) return;
    play(older);
  }, [play]);

  const hasOlderEpisode = useMemo(() => {
    if (!currentEpisode) return false;
    const list = getPublishedEpisodes();
    const index = list.findIndex((ep) => ep.buzzsproutId === currentEpisode.buzzsproutId);
    return index >= 0 && index < list.length - 1;
  }, [currentEpisode]);

  useEffect(() => {
    if (!('mediaSession' in navigator)) return;
    const ms = navigator.mediaSession;
    ms.setActionHandler('play', () => togglePlayPause());
    ms.setActionHandler('pause', () => pause());
    ms.setActionHandler('seekforward', (details) => skipForward(details.seekOffset));
    ms.setActionHandler('seekbackward', (details) => skipBackward(details.seekOffset));
    ms.setActionHandler('seekto', (details) => {
      if (details.seekTime != null) seek(details.seekTime);
    });
    return () => {
      ms.setActionHandler('play', null);
      ms.setActionHandler('pause', null);
      ms.setActionHandler('seekforward', null);
      ms.setActionHandler('seekbackward', null);
      ms.setActionHandler('seekto', null);
    };
  }, [togglePlayPause, pause, skipForward, skipBackward, seek]);

  const state = useMemo<AudioPlayerState>(
    () => ({
      currentEpisode,
      isPlaying,
      currentTime,
      duration,
      volume,
      isMuted,
      isLoading,
      error,
      inlineVisible,
      playbackRate,
      hasOlderEpisode,
    }),
    [
      currentEpisode,
      isPlaying,
      currentTime,
      duration,
      volume,
      isMuted,
      isLoading,
      error,
      inlineVisible,
      playbackRate,
      hasOlderEpisode,
    ],
  );

  const controls = useMemo<AudioPlayerControls>(
    () => ({
      play,
      pause,
      togglePlayPause,
      seek,
      skipForward,
      skipBackward,
      setVolume,
      toggleMute,
      close,
      setInlineVisible,
      setPlaybackRate,
      playOlderEpisode,
    }),
    [
      play,
      pause,
      togglePlayPause,
      seek,
      skipForward,
      skipBackward,
      setVolume,
      toggleMute,
      close,
      setInlineVisible,
      setPlaybackRate,
      playOlderEpisode,
    ],
  );

  return (
    <AudioPlayerControlsContext.Provider value={controls}>
      <AudioPlayerStateContext.Provider value={state}>
        {children}
        <audio ref={audioRef} preload="metadata" />
        <div
          role="status"
          aria-live="polite"
          className="sr-only"
        >
          {announcement}
        </div>
      </AudioPlayerStateContext.Provider>
    </AudioPlayerControlsContext.Provider>
  );
}

function setMediaSessionMetadata(episode: PublishedEpisode) {
  if (!('mediaSession' in navigator)) return;
  try {
    const artworkSrc = episode.artworkUrl ?? episode.image.src;
    navigator.mediaSession.metadata = new MediaMetadata({
      title: episode.title,
      artist: episode.host,
      artwork: artworkSrc ? [{ src: artworkSrc, sizes: '512x512' }] : undefined,
    });
  } catch (err) {
    console.warn('[AudioPlayer] MediaSession metadata failed', err);
  }
}
